import { useCallback, useEffect, useState } from "react";
import { Loader2, ChevronDown, ChevronRight, RefreshCw, FileClock } from "lucide-react";
import { api } from "../../../lib/api";

const OP_COLOR = {
    create: "bg-emerald-100 text-emerald-800",
    update: "bg-blue-100 text-blue-800",
    soft_delete: "bg-rose-100 text-rose-800",
    hard_delete: "bg-red-200 text-red-900",
    restore: "bg-violet-100 text-violet-800",
    publish: "bg-amber-100 text-amber-800",
    bulk_import: "bg-cyan-100 text-cyan-800",
    rollback: "bg-orange-100 text-orange-800",
    restore_backup: "bg-fuchsia-100 text-fuchsia-800",
};

export default function AuditLogsPanel({ onRollback }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState(new Set());
    const [filterKind, setFilterKind] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const qs = new URLSearchParams({ limit: "200" });
            if (filterKind) qs.set("kind", filterKind);
            const r = await api.get(`/admin/master-ops/audit?${qs.toString()}`);
            setRows(r.data.items || []);
        } finally { setLoading(false); }
    }, [filterKind]);

    useEffect(() => { load(); }, [load]);

    const toggle = (id) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    return (
        <div className="bg-white rounded-2xl border border-slate-200" data-testid="md-audit-panel">
            <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2">
                <FileClock className="w-4 h-4 text-slate-500" />
                <h3 className="font-heading text-sm font-bold text-[#0F172A]">Audit Logs — Full Change History</h3>
                <select
                    value={filterKind}
                    onChange={(e) => setFilterKind(e.target.value)}
                    data-testid="md-audit-filter-kind"
                    className="ml-auto text-xs px-2 py-1.5 rounded border border-slate-200"
                >
                    <option value="">All kinds</option>
                    {["subject", "grade", "board", "country", "state", "city", "pincode",
                        "language", "exam_category", "curriculum_type", "curriculum_pack"].map((k) =>
                        <option key={k} value={k}>{k}</option>)}
                </select>
                <button onClick={load} data-testid="md-audit-refresh" className="text-xs px-2 py-1.5 rounded border border-slate-200 inline-flex items-center gap-1 hover:bg-slate-50">
                    <RefreshCw className="w-3 h-3" /> Refresh
                </button>
            </div>
            <div className="max-h-[65vh] overflow-y-auto divide-y divide-slate-100">
                {loading && (
                    <div className="px-4 py-6 text-sm text-slate-500 flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                    </div>
                )}
                {!loading && rows.length === 0 && (
                    <div className="px-4 py-8 text-sm text-slate-500 text-center">No audit entries yet.</div>
                )}
                {!loading && rows.map((r) => {
                    const id = r.id;
                    const opCls = OP_COLOR[r.op] || "bg-slate-100 text-slate-700";
                    const isOpen = expanded.has(id);
                    const diff = r.diff || {};
                    const diffKeys = Object.keys(diff).filter((k) => k !== "_op");
                    return (
                        <div key={id} className="px-4 py-2.5 text-xs" data-testid={`md-audit-row-${id}`}>
                            <button onClick={() => toggle(id)} className="w-full flex items-center gap-2 text-left">
                                {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                                <span className={`px-1.5 py-0.5 rounded font-bold uppercase ${opCls}`}>{r.op}</span>
                                <span className="font-semibold text-slate-800">{r.kind}</span>
                                <span className="text-slate-400">·</span>
                                <span className="text-slate-600 truncate">{r.actor_email}</span>
                                <span className="ml-auto text-slate-400 text-[10px]">
                                    {new Date(r.at).toLocaleString()}
                                </span>
                            </button>
                            {isOpen && (
                                <div className="mt-2 ml-5 bg-slate-50 rounded-lg p-2 space-y-1">
                                    <div className="text-[10px] text-slate-500">item_id: <code className="text-slate-700">{r.item_id}</code></div>
                                    {diffKeys.length === 0 && (
                                        <div className="text-slate-500 italic">No field-level changes recorded.</div>
                                    )}
                                    {diffKeys.map((k) => {
                                        const v = diff[k];
                                        if (typeof v === "object" && v && "before" in v) {
                                            return (
                                                <div key={k} className="flex flex-wrap items-baseline gap-1.5">
                                                    <span className="font-semibold text-slate-700">{k}:</span>
                                                    <span className="line-through text-rose-600 break-all">{JSON.stringify(v.before)}</span>
                                                    <span className="text-slate-400">→</span>
                                                    <span className="text-emerald-700 break-all">{JSON.stringify(v.after)}</span>
                                                </div>
                                            );
                                        }
                                        return <div key={k}><span className="font-semibold text-slate-700">{k}:</span> {JSON.stringify(v)}</div>;
                                    })}
                                    {r.op === "update" && onRollback && (
                                        <button
                                            onClick={() => onRollback(r)}
                                            data-testid={`md-audit-rollback-${id}`}
                                            className="mt-2 text-[10px] px-2 py-1 rounded bg-orange-100 text-orange-800 font-semibold hover:bg-orange-200"
                                        >
                                            ↶ Rollback to &quot;before&quot; state
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            <div className="px-4 py-2 border-t border-slate-100 text-[11px] text-slate-500">{rows.length} entries</div>
        </div>
    );
}
